import { CalendarEventDetails, CalendarEventGuest } from '@griply/types';

// The organizer is not reliably part of `guests` — Google lists them as an
// attendee, Outlook does not — so the display list merges both sources.
export type CalendarEventGuestListEntry = CalendarEventGuest & {
  isOrganizer: boolean;
};

export type GetCalendarEventGuestListOptions = {
  guests: CalendarEventGuest[];
  organizer?: CalendarEventDetails['organizer'];
  // Whether the viewer organizes the event. Their own name badged "Organizer" in
  // their own event is noise, so the merge is skipped — but they stay in the list
  // as a plain guest when the provider also lists them as an attendee.
  isOrganizer?: CalendarEventDetails['isOrganizer'];
};

export const getCalendarEventGuestList = ({
  guests,
  organizer,
  isOrganizer,
}: GetCalendarEventGuestListOptions): CalendarEventGuestListEntry[] => {
  if (!organizer || isOrganizer) {
    return guests.map((guest) => ({ ...guest, isOrganizer: false }));
  }

  const organizerEmail = organizer.email.toLowerCase();
  const existing = guests.find(
    ({ email }) => email.toLowerCase() === organizerEmail,
  );

  // Their guest entry carries the RSVP the provider recorded, so it wins over
  // the organizer field — which only ever has an email and a name.
  const organizerEntry: CalendarEventGuestListEntry = existing
    ? { ...existing, isOrganizer: true }
    : {
        email: organizer.email,
        ...(organizer.name && { name: organizer.name }),
        isOrganizer: true,
      };

  return [
    organizerEntry,
    ...guests
      .filter(({ email }) => email.toLowerCase() !== organizerEmail)
      .map((guest) => ({ ...guest, isOrganizer: false })),
  ];
};
